import { protectedAxios, publicAxios } from "@shared/src/utils/axios";
import { handleServiceError } from "@shared/src/utils/error-handler";
import type {
  BackendAccount,
  CheckUsernameRequest,
  UpdateUsernameRequest,
  LinkAccountRequest,
  UpdateEmailRequest,
} from "@sdk/shared/types/settings.types";
import { poll } from "@shared/src/lib/auth";
import type { ErrorResponse } from "@shared/src/utils/error-handler";

export const settingsService = {
  getAccount: async (jwt?: string) => {
    const fetchAccount = async () => {
      const res = jwt
        ? await publicAxios.get<BackendAccount>("/auth/account", {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          })
        : await protectedAxios.get<BackendAccount>("/auth/account");
      if (!res.data) {
        throw new Error("Failed to get account details");
      }
      return res.data;
    };

    return handleServiceError(async () => {
      const res = await poll(fetchAccount, 1000, 5);
      if (!res) {
        throw new Error("Failed to get account details");
      }
      return res;
    });
  },

  checkUsername: async (payload: CheckUsernameRequest) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post<null>("/auth/username/check", payload);
      if (data) {
        throw new Error("Failed to check username availability");
      }
      return data;
    });
  },

  updateUsername: async (payload: UpdateUsernameRequest) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post<BackendAccount>("/auth/username", payload);
      if (!data) {
        throw new Error("Failed to update username");
      }
      return data;
    });
  },
  updateEmail: async (payload: UpdateEmailRequest) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post<BackendAccount>("/auth/email", payload);
      if (!data) {
        throw new Error("Failed to update email");
      }
      return data;
    });
  },

  linkAccount: async (payload: LinkAccountRequest) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post<BackendAccount>("/auth/link", payload);
      if (!data) {
        throw new Error("Failed to link accounts");
      }
      return data;
    });
  },
  isWhitelisted: async (payload: { id: string; jwt?: string }) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post<boolean>(
        "/auth/allowed",
        { id: payload.id },
        {
          headers: {
            ...(payload.jwt && { Authorization: `Bearer ${payload.jwt}` }),
          },
        },
      );
      if (!data) {
        throw new Error("Failed to check if user is whitelisted");
      }
      return data;
    });
  },

  /**
   * Returns the telegram username linked to the currently authenticated wallet (if any).
   * Makes a GET request to `/telegram/registered` endpoint.
   */
  getTelegramRegistration: async (): Promise<ErrorResponse<{ username: string }>> => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.get<{ username: string }>("/telegram/registered");
      return data;
    });
  },

  /**
   * Check if the currently signed-in user has already linked a Telegram chat for notifications.
   * Convenience wrapper around `getAccount()` so we avoid duplicating account-level logic across the app.
   *
   * @param jwt Optional JWT token to be passed explicitly. When omitted the helper falls back to the standard
   *            cookie-based authentication mechanism inside `protectedAxios` – useful for browser environments.
   * @returns `true` when at least one Telegram chat is linked **and** marked as active; otherwise `false`.
   */
  hasActiveTelegram: async (jwt?: string): Promise<boolean> => {
    const { data: account } = await settingsService.getAccount(jwt);

    // Defensive guards – backend SHOULD always send `addresses`, but we keep this to avoid runtime errors.
    if (!account || !Array.isArray(account.addresses)) {
      return false;
    }

    // Return `true` if ANY of the user's addresses has an active Telegram chat.
    return account.addresses.some(
      address => Array.isArray(address.telegramChats) && address.telegramChats.some(chat => chat?.isActive === true),
    );
  },
};